import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { TableSkeleton } from "../components/Loading";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { leadsApi, LEAD_CLASSIFICATIONS, type Lead, type LeadClassification, type LeadStage } from "../api/leads";
import { tmApi, type TmUserOption } from "../api/tm";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import Ic from "../components/icons.jsx";
import { LeadStageBadge } from "../components/LeadStageBadge";
import { LeadClassificationChip } from "../components/LeadClassificationChip";
import { CountrySelect } from "../components/CountrySelect";
import { Select } from "../components/Select";
import { LeadsFilterBar } from "../components/leads/LeadsFilterBar";
import { LeadsViewSwitcher } from "../components/leads/LeadsViewSwitcher";
import { countryName, flagEmoji } from "../i18n/countries";
import { LEAD_STAGES } from "./leadPipeline";
import {
  filterLeads, hasActiveLeadFilter, distinctOwners, distinctCountries,
  EMPTY_LEAD_FILTER, type LeadFilterCriteria,
} from "./leadsFilter";
import { formatMoney } from "../lib/format";

/** Currencies offered in the create form's small currency select. */
const CURRENCIES = ["EUR", "RSD", "USD"] as const;

/**
 * Lidovi (leads) list — internal sales lead pipeline. The backend RLS-scopes the list to the internal company (all sales staff
 * share one pool: "svi iz prodaje vide sve"), so the page loads the full list
 * and filters client-side. "+ Nov lid" opens a grouped, firm-first create modal;
 * rows link to the detail page with the pipeline stepper.
 */
export default function LeadsPage() {
  const { canWrite } = useSectionAccess("leads");
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const navigate = useNavigate();
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Lead[] | null>(null);
  // ?new=1 (the board's "+ Nov lid" hand-off) opens the create modal on load.
  const [creating, setCreating] = useState(() => canWrite && searchParams.get("new") === "1");
  const [criteria, setCriteria] = useState<LeadFilterCriteria>(() => ({
    ...EMPTY_LEAD_FILTER,
    search: searchParams.get("q") ?? "",
  }));

  // Same "q"-only URL-sync idiom as the offers views — keeps the search text
  // alive across the board<->list switch (see LeadsViewSwitcher).
  const setSearch = (v: string) => {
    setCriteria((c) => ({ ...c, search: v }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set("q", v); else next.delete("q");
      return next;
    }, { replace: true });
  };

  async function load() {
    try {
      setItems(await leadsApi.list());
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const owners = useMemo(() => distinctOwners(items ?? []), [items]);
  const countries = useMemo(() => distinctCountries(items ?? []), [items]);
  const showOwnerFilter = owners.length > 1;
  const filtered = useMemo(() => filterLeads(items ?? [], criteria), [items, criteria]);
  const filtersActive = hasActiveLeadFilter(criteria);

  function fmtValue(l: Lead): string {
    if (l.value == null) return "—";
    return formatMoney(l.value, l.currency);
  }

  function fmtContact(l: Lead): string {
    return [l.email, l.phone].filter(Boolean).join(" · ") || "—";
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <header style={pageHead}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("leads.list.title", "Leads")}</h1>
          <div style={subText}>
            {items === null
              ? t("common.loading")
              : t("leads.list.summary", { count: items.length, defaultValue: "{{count}} leads" })}
          </div>
        </div>
        <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setCreating(true)}>
          <Ic.plus /> {t("leads.list.new", "New lead")}
        </button>
      </header>

      <LeadsViewSwitcher active="list" search={criteria.search} />

      {items === null ? (
        <TableSkeleton rows={6} cols={5} label={t("common.loading")} />
      ) : items.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>🌱</div>
          <b style={{ color: "var(--ink-2)" }}>{t("leads.empty.title", "No leads yet.")}</b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {t("leads.empty.hint", "Add your first potential customer.")}
          </div>
          <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setCreating(true)} style={{ marginTop: 12 }}>
            <Ic.plus /> {t("leads.list.new", "New lead")}
          </button>
        </div>
      ) : (
        <>
          <LeadsFilterBar
            criteria={criteria}
            onSearchChange={setSearch}
            onStageChange={(v) => setCriteria((c) => ({ ...c, stage: v }))}
            onOwnerChange={(v) => setCriteria((c) => ({ ...c, ownerId: v }))}
            onCountryChange={(v) => setCriteria((c) => ({ ...c, country: v }))}
            onClassificationChange={(v) => setCriteria((c) => ({ ...c, classification: v }))}
            owners={owners}
            countries={countries}
            showOwnerFilter={showOwnerFilter}
            shownCount={filtered.length}
            totalCount={items.length}
            filtersActive={filtersActive}
            onClear={() => setCriteria((c) => ({ ...EMPTY_LEAD_FILTER, search: c.search }))}
          />

          {filtered.length === 0 ? (
            <div style={emptyCard}>
              <b style={{ color: "var(--ink-2)" }}>{t("leads.filter.empty", "No leads match the filters.")}</b>
            </div>
          ) : (
            <div className="card" style={{ overflow: "hidden", padding: 0 }}>
              <table style={tbl} className="list-card">
                <thead>
                  <tr>
                    <th style={th}>{t("leads.table.name", "Firm")}</th>
                    <th style={th}>{t("leads.table.contact", "Contact")}</th>
                    <th style={th}>{t("leads.table.country", "Country")}</th>
                    <th style={th}>{t("leads.table.classification", "Classification")}</th>
                    <th style={th}>{t("leads.table.stage", "Stage")}</th>
                    <th style={th}>{t("leads.table.owner", "Owner")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("leads.table.value", "Value")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)} style={{ cursor: "pointer" }} className="row-link">
                      <td style={td} data-label={t("leads.table.name", "Firm")}>
                        <b style={{ color: "var(--ink)" }}>{l.name}</b>
                        {l.convertedCompanyId && (
                          <span style={convertedChip}>{t("leads.badge.converted", "Converted")}</span>
                        )}
                        {(l.contactPerson || l.contactPosition) && (
                          <div style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 2 }}>
                            {[l.contactPerson, l.contactPosition].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, color: "var(--ink-2)", fontSize: 12.5 }} data-label={t("leads.table.contact", "Contact")}>
                        {fmtContact(l)}
                      </td>
                      <td style={{ ...td, color: "var(--ink-2)" }} data-label={t("leads.table.country", "Country")}>
                        {l.country
                          ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span aria-hidden>{flagEmoji(l.country)}</span>{countryName(l.country, locale)}
                            </span>
                          : "—"}
                      </td>
                      <td style={td} data-label={t("leads.table.classification", "Classification")}>
                        {l.classification ? <LeadClassificationChip classification={l.classification} /> : "—"}
                      </td>
                      <td style={td} data-label={t("leads.table.stage", "Stage")}>
                        <LeadStageBadge stage={l.stage} />
                      </td>
                      <td style={{ ...td, color: "var(--ink-2)" }} data-label={t("leads.table.owner", "Owner")}>
                        {l.ownerName ?? "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: "var(--ink)", fontWeight: 600 }} data-label={t("leads.table.value", "Value")}>
                        {fmtValue(l)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {creating && canWrite && (
        <NewLeadModal
          onClose={() => setCreating(false)}
          onCreated={(lead) => {
            setCreating(false);
            navigate(`/leads/${lead.id}`);
          }}
        />
      )}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated }: { onClose: () => void; onCreated: (lead: Lead) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPosition, setContactPosition] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [classification, setClassification] = useState<"" | LeadClassification>("");
  const [source, setSource] = useState("");
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState<string>("EUR");
  const [ownerId, setOwnerId] = useState(""); // "" = default to the creator server-side
  const [stage, setStage] = useState<LeadStage>("NOV");
  const [note, setNote] = useState("");
  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [busy, setBusy] = useState(false);

  // Owner picker of internal staff — /api/tm/users is reachable by SALES
  // (tm:access, held by every internal role). If it fails, owner stays the
  // creator via the empty default option.
  useEffect(() => {
    tmApi.users().then(setUsers).catch(() => setUsers([]));
  }, []);

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const parsedValue = value.trim() === "" ? null : Number(value);
      const lead = await leadsApi.create({
        name: name.trim(),
        contactPerson: contactPerson.trim() || null,
        contactPosition: contactPosition.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        country: country || null,
        addressLine1: addressLine1.trim() || null,
        postalCode: postalCode.trim() || null,
        city: city.trim() || null,
        classification: classification || null,
        source: source.trim() || null,
        value: parsedValue != null && Number.isFinite(parsedValue) ? parsedValue : null,
        currency,
        ownerId: ownerId || null,
        stage,
        note: note.trim() || null,
      });
      toast.success(t("leads.toast.created", "Lead created."));
      onCreated(lead);
    } catch (err) {
      toast.error((err as ApiError).message);
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={t("leads.new.title", "New lead")}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={panelHead}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>{t("leads.new.title", "New lead")}</h2>
          <button type="button" className="btn sm" onClick={onClose} aria-label={t("action.cancel", "Cancel")}>
            <Ic.x />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={t("leads.field.name", "Company name")} required>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={300} />
          </Field>
          <div style={row}>
            <Field label={t("leads.field.contactPerson", "Contact person")} style={col}>
              <input className="input" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} maxLength={200} />
            </Field>
            <Field label={t("leads.field.contactPosition", "Position")} style={col}>
              <input
                className="input"
                value={contactPosition}
                onChange={(e) => setContactPosition(e.target.value)}
                maxLength={150}
                placeholder={t("leads.field.contactPositionPlaceholder", "e.g. Head of purchasing")}
              />
            </Field>
          </div>

          <div style={row}>
            <Field label={t("leads.field.email", "Email")} style={col}>
              <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" maxLength={320} />
            </Field>
            <Field label={t("leads.field.phone", "Phone")} style={col}>
              <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={60} />
            </Field>
          </div>

          <div style={row}>
            <Field label={t("leads.field.country", "Country")} style={col}>
              <CountrySelect value={country} onChange={setCountry} ariaLabel={t("leads.field.country", "Country")} />
            </Field>
            <Field label={t("leads.field.classification", "Classification")} style={col}>
              <Select<"" | LeadClassification>
                value={classification}
                onChange={setClassification}
                ariaLabel={t("leads.field.classification", "Classification")}
                options={[
                  { value: "", label: t("leads.classification.none", "—") },
                  ...LEAD_CLASSIFICATIONS.map((c) => ({ value: c, label: t(`leads.classification.${c}`, c) })),
                ]}
              />
            </Field>
          </div>

          <Field label={t("leads.field.address", "Address")}>
            <input className="input" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} maxLength={200} />
          </Field>
          <div style={row}>
            <Field label={t("leads.field.postalCode", "Postal code")} style={{ width: 140 }}>
              <input className="input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={20} />
            </Field>
            <Field label={t("leads.field.city", "City")} style={col}>
              <input className="input" value={city} onChange={(e) => setCity(e.target.value)} maxLength={120} />
            </Field>
          </div>

          <div style={row}>
            <Field label={t("leads.field.source", "Source")} style={col}>
              <input className="input" value={source} onChange={(e) => setSource(e.target.value)} maxLength={200} />
            </Field>
            <Field label={t("leads.field.value", "Value")} style={col}>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="input" value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" style={{ flex: 1 }} />
                <Select<string>
                  value={currency}
                  onChange={setCurrency}
                  ariaLabel={t("leads.field.currency", "Currency")}
                  style={{ width: 92 }}
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                />
              </div>
            </Field>
          </div>

          <div style={row}>
            <Field label={t("leads.field.owner", "Owner")} style={col}>
              <Select<string>
                value={ownerId}
                onChange={setOwnerId}
                ariaLabel={t("leads.field.owner", "Owner")}
                options={[
                  { value: "", label: t("leads.new.ownerSelf", "Me (creator)") },
                  ...users.map((u) => ({ value: u.id, label: u.name })),
                ]}
              />
            </Field>
            <Field label={t("leads.field.stage", "Stage")} style={col}>
              <Select<LeadStage>
                value={stage}
                onChange={setStage}
                ariaLabel={t("leads.field.stage", "Stage")}
                options={LEAD_STAGES.map((s) => ({ value: s, label: t(`leads.stage.${s}`, s) }))}
              />
            </Field>
          </div>

          <Field label={t("leads.field.note", "Note")}>
            <textarea className="input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={5000} style={{ resize: "vertical" }} />
          </Field>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="btn primary" onClick={() => void submit()} disabled={busy || !name.trim()}>
            {busy ? t("leads.new.saving", "Saving…") : t("leads.new.create", "Create lead")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label, required, children, style,
}: { label: string; required?: boolean; children: React.ReactNode; style?: CSSProperties }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
      <span style={fieldLabel}>
        {label}
        {required && <span style={{ color: "var(--err)" }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const pageHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" };
const subText: CSSProperties = { fontSize: 13, color: "var(--ink-3)", marginTop: 4 };
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = { textAlign: "left", padding: "11px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid var(--border)" };
const td: CSSProperties = { padding: "12px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const emptyCard: CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
const convertedChip: CSSProperties = { marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "var(--ok)", background: "var(--ok-bg)", padding: "1px 7px", borderRadius: 999, verticalAlign: "middle" };
const overlay: CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "grid", placeItems: "center", padding: 20, zIndex: 60 };
const panel: CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)", boxShadow: "var(--shadow-2)", padding: 22, width: 560, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" };
const panelHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 };
const fieldLabel: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const row: CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
const col: CSSProperties = { flex: 1, minWidth: 160 };
